import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";

import useAgentStore from "./useAgentStore";
import usePublicChatStore from "./usePublicChatStore";
import useTaskQueueStore from "./useTaskQueueStore";
import useChatStreamStore from "./useChatStreamStore";
import ConversationWorkspace from "./conversation/ConversationWorkspace";
import { buildAgentSessionCapabilities } from "./conversation/conversationAdapters";
import TaskQueuePanel from "./taskQueue/TaskQueuePanel";
import { useStatusPalette } from "../../utils/statusColors";

import {
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
  CHAT_AREA_MIN_WIDTH,
  SIDEBAR_RESIZE_HANDLE_WIDTH,
  TASK_QUEUE_MIN_WIDTH,
  TASK_QUEUE_MAX_WIDTH,
  TASK_QUEUE_RESIZE_HANDLE_WIDTH,
  STATUS_WAITING_FOR_REVIEW,
  PanelRoot,
  Sidebar,
  SidebarList,
  ChatArea,
  normalizeChatUiPolicy,
  parsePublicErrorMessage,
} from "./agentSessionsPanelUtils";

import useTaskQueuePanel from "./useTaskQueuePanel";
import SessionRow from "./SessionRow";
import NewChatComposer from "./NewChatComposer";
import PendingChatView from "./PendingChatView";
import ScheduledTab from "./ScheduledTab";

function AgentSessionsPanel({
  agentId,
  agentName,
  agentDescription,
  agentInboxAddress,
  chatUiPolicy,
  initialSessionId,
  onSessionSelect,
  onConfigOpen,
  mode = "private",
  publicShareToken = "",
  publicIdentityPayload = {},
  initialPublicSessionToken = "",
  onPublicSessionTokenChange,
  composerDisabled = false,
  debugModeEnabled = false,
  showSessionSidebar = true,
  newChatRequestNonce = 0,
  selectSessionRequest = null,
  taskQueueOpenSignal = 0,
  onTaskQueueToggleStateChange,
  inlineTaskQueueToggle = true,
  headerActions = null,
  agentModel,
  chatContext,
  welcomePanelContent = null,
  hideWelcomeComposerInput = false,
  publicUserDisplayName = "",
}) {
  const _sp = useStatusPalette();
  const storeSessions = useAgentStore(state => state.sessions);
  const storeSessionsLoading = useAgentStore(state => state.sessionsLoading);
  const storeSessionsHasMore = useAgentStore(state => state.sessionsHasMore);
  const storeSessionsLoadingMore = useAgentStore(state => state.sessionsLoadingMore);
  const storeSessionDetail = useAgentStore(state => state.sessionDetail);
  const storeSessionDetailLoading = useAgentStore(state => state.sessionDetailLoading);
  const storeTaskQueueEventsBySessionId = useTaskQueueStore(state => state.taskQueueEventsBySessionId);
  const storeSessionStreamStateBySessionId = useChatStreamStore(state => state.sessionStreamStateBySessionId);

  const panelRootRef = useRef(null);
  const isResizingSidebarRef = useRef(false);
  const isResizingTaskQueueRef = useRef(false);
  const [selectedSessionId, setSelectedSessionId] = useState(
    initialSessionId || null
  );
  const [isNewChat, setIsNewChat] = useState(false);
  const [sidebarTab, setSidebarTab] = useState("sessions");
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const [startingChat, setStartingChat] = useState(false);

  const isPublicMode = mode === "public";
  const [selectedPublicSessionToken, setSelectedPublicSessionToken] = useState(
    initialPublicSessionToken || ""
  );
  const selectedPublicSessionTokenRef = useRef(initialPublicSessionToken || "");
  const [publicSessions, setPublicSessions] = useState([]);
  const [publicSessionsLoading, setPublicSessionsLoading] = useState(false);
  const [publicSessionDetail, setPublicSessionDetail] = useState(null);
  const [publicSessionDetailLoading, setPublicSessionDetailLoading] =
    useState(false);
  const [publicPendingUserMessage, setPublicPendingUserMessage] =
    useState(null);
  const [
    publicSessionTokenByChatSessionId,
    setPublicSessionTokenByChatSessionId,
  ] = useState({});
  const [publicError, setPublicError] = useState("");

  const isNarrowQueueMode = useMediaQuery("(max-width:1100px)");
  const effectiveChatUiPolicy = {
    ...normalizeChatUiPolicy(chatUiPolicy),
    ...(publicUserDisplayName ? { publicUserDisplayName } : {}),
  };
  const taskQueuePlannerUiEnabled =
    effectiveChatUiPolicy.showTaskQueuePlannerInChat === true;
  const showSidebar = showSessionSidebar;

  const handleOpenAgentSettings = useCallback(
    (targetAgentId = null) => {
      if (!onConfigOpen) return;
      onConfigOpen(targetAgentId || agentId);
    },
    [agentId, onConfigOpen]
  );

  const notifyPublicSessionTokenChange = useCallback(
    (nextToken) => {
      if (!isPublicMode) return;
      if (onPublicSessionTokenChange) {
        onPublicSessionTokenChange(nextToken || "");
      }
    },
    [isPublicMode, onPublicSessionTokenChange]
  );

  const upsertPublicSessionTokenMapping = useCallback(
    (chatSessionId, publicSessionToken) => {
      if (!chatSessionId || !publicSessionToken) return;
      setPublicSessionTokenByChatSessionId((current) => {
        if (current[chatSessionId] === publicSessionToken) {
          return current;
        }
        return {
          ...current,
          [chatSessionId]: publicSessionToken,
        };
      });
    },
    []
  );

  const resolvePrivateClientId = useCallback(() => {
    if (isPublicMode) return null;
    const currentDetail = useAgentStore.getState().sessionDetail;
    if (
      currentDetail &&
      currentDetail.chatSessionId === selectedSessionId &&
      Number.isFinite(Number(currentDetail.clientId)) &&
      Number(currentDetail.clientId) > 0
    ) {
      return Number(currentDetail.clientId);
    }
    const selectedRow = useAgentStore.getState().sessions.find(
      (session) => session?.chatSessionId === selectedSessionId
    );
    const rowClientId = Number(selectedRow?.clientId);
    if (Number.isFinite(rowClientId) && rowClientId > 0) {
      return rowClientId;
    }
    return null;
  }, [isPublicMode, selectedSessionId]);

  // --- Task Queue Panel (extracted hook) ---

  const tq = useTaskQueuePanel({
    selectedSessionId,
    isPublicMode,
    selectedPublicSessionToken,
    publicIdentityPayload,
    taskQueuePlannerUiEnabled,
    taskQueueOpenSignal,
    isNarrowQueueMode,
    resolvePrivateClientId,
    storeTaskQueueEventsBySessionId,
    storeSessionStreamStateBySessionId,
  });

  // --- Public session logic ---

  const loadPublicSessionDetail = useCallback(
    async (publicSessionToken, { silent = false } = {}) => {
      if (!isPublicMode || !publicSessionToken) return null;
      if (!silent) {
        setPublicSessionDetailLoading(true);
      }
      try {
        const response = await usePublicChatStore.getState().getPublicChatSession(
          publicSessionToken,
          publicIdentityPayload || {}
        );
        const nextSession = response?.data || null;
        setPublicSessionDetail((currentSession) => {
          if (!nextSession) return null;
          if (
            nextSession.status === STATUS_WAITING_FOR_REVIEW &&
            !nextSession.blockedWhileReviewMessage &&
            currentSession?.blockedWhileReviewMessage
          ) {
            return {
              ...nextSession,
              blockedWhileReviewMessage:
                currentSession.blockedWhileReviewMessage,
            };
          }
          return nextSession;
        });
        setPublicError("");
        if (nextSession?.chatSessionId) {
          setSelectedSessionId(nextSession.chatSessionId);
          upsertPublicSessionTokenMapping(
            nextSession.chatSessionId,
            publicSessionToken
          );
          if (onSessionSelect) {
            onSessionSelect(nextSession.chatSessionId);
          }
        }
        setPublicPendingUserMessage((currentPendingMessage) => {
          if (!currentPendingMessage || !Array.isArray(nextSession?.messages)) {
            return currentPendingMessage;
          }
          const hasPendingBeenPersisted = nextSession.messages.some(
            (chatMessage) =>
              chatMessage?.role === "user" &&
              String(chatMessage?.content || "") === currentPendingMessage
          );
          return hasPendingBeenPersisted ? null : currentPendingMessage;
        });
        return nextSession;
      } catch (error) {
        const errorMessage = parsePublicErrorMessage(
          error,
          "Unable to load this public chat session."
        );
        setPublicError(errorMessage);
        const status = error?.response?.status;
        if (status === 404 || status === 410 || status === 403) {
          setSelectedSessionId(null);
          setSelectedPublicSessionToken("");
          setPublicSessionDetail(null);
          setPublicPendingUserMessage(null);
          notifyPublicSessionTokenChange("");
        }
        return null;
      } finally {
        if (!silent) {
          setPublicSessionDetailLoading(false);
        }
      }
    },
    [
      isPublicMode,
      notifyPublicSessionTokenChange,
      onSessionSelect,
      publicIdentityPayload,
      upsertPublicSessionTokenMapping,
    ]
  );

  const loadPublicSessions = useCallback(
    async ({ silent = false } = {}) => {
      if (!isPublicMode || !publicShareToken || !showSidebar) return [];
      if (!silent) {
        setPublicSessionsLoading(true);
      }
      try {
        const response = await usePublicChatStore.getState().listPublicChatSessions(
          publicShareToken,
          publicIdentityPayload || {}
        );
        const nextSessions = Array.isArray(response?.data) ? response.data : [];
        setPublicSessions(nextSessions);
        setPublicError("");
        setPublicSessionTokenByChatSessionId((current) => {
          const next = { ...current };
          nextSessions.forEach((session) => {
            if (session?.chatSessionId && session?.publicSessionToken) {
              next[session.chatSessionId] = session.publicSessionToken;
            }
          });
          return next;
        });
        const activePublicSessionToken = selectedPublicSessionTokenRef.current;
        if (activePublicSessionToken) {
          const matchingSession = nextSessions.find(
            (session) =>
              session?.publicSessionToken === activePublicSessionToken
          );
          if (matchingSession?.chatSessionId) {
            setSelectedSessionId(matchingSession.chatSessionId);
          }
        }
        return nextSessions;
      } catch (error) {
        setPublicSessions([]);
        setPublicError(
          parsePublicErrorMessage(error, "Unable to load public chat sessions.")
        );
        return [];
      } finally {
        if (!silent) {
          setPublicSessionsLoading(false);
        }
      }
    },
    [isPublicMode, publicShareToken, publicIdentityPayload, showSidebar]
  );

  useEffect(() => {
    selectedPublicSessionTokenRef.current = selectedPublicSessionToken;
  }, [selectedPublicSessionToken]);

  // --- Resize logic ---

  const getSidebarBounds = useCallback(() => {
    const containerRect = panelRootRef.current?.getBoundingClientRect();
    if (!containerRect) {
      return { min: SIDEBAR_MIN_WIDTH, max: SIDEBAR_MAX_WIDTH };
    }
    const isCompact = containerRect.width < 920;
    const minChatWidth = isCompact
      ? Math.max(120, Math.floor(containerRect.width * 0.45))
      : CHAT_AREA_MIN_WIDTH;
    const minSidebarWidth = isCompact
      ? Math.max(100, Math.floor(containerRect.width * 0.3))
      : SIDEBAR_MIN_WIDTH;
    const sidebarSpaceCap = Math.max(
      0,
      containerRect.width - minChatWidth - SIDEBAR_RESIZE_HANDLE_WIDTH
    );
    const maxSidebarWidth = Math.max(
      minSidebarWidth,
      Math.min(SIDEBAR_MAX_WIDTH, sidebarSpaceCap)
    );
    const effectiveMinSidebarWidth = Math.min(minSidebarWidth, maxSidebarWidth);
    return { min: effectiveMinSidebarWidth, max: maxSidebarWidth };
  }, []);

  const clampSidebarWidth = useCallback(
    (candidateWidth) => {
      const { min, max } = getSidebarBounds();
      return Math.max(min, Math.min(max, candidateWidth));
    },
    [getSidebarBounds]
  );

  const handleSidebarResizeStart = (event) => {
    if (!showSidebar) return;
    event.preventDefault();
    isResizingSidebarRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const getTaskQueueBounds = useCallback(() => {
    const containerRect = panelRootRef.current?.getBoundingClientRect();
    if (!containerRect) {
      return { min: TASK_QUEUE_MIN_WIDTH, max: TASK_QUEUE_MAX_WIDTH };
    }
    const isCompact = containerRect.width < 1100;
    const minChatWidth = isCompact
      ? Math.max(220, Math.floor(containerRect.width * 0.45))
      : CHAT_AREA_MIN_WIDTH;
    const minQueueWidth = isCompact
      ? Math.max(220, Math.floor(containerRect.width * 0.32))
      : TASK_QUEUE_MIN_WIDTH;
    const queueSpaceCap = Math.max(
      0,
      containerRect.width -
        minChatWidth -
        SIDEBAR_RESIZE_HANDLE_WIDTH -
        TASK_QUEUE_RESIZE_HANDLE_WIDTH
    );
    const maxQueueWidth = Math.max(
      minQueueWidth,
      Math.min(TASK_QUEUE_MAX_WIDTH, queueSpaceCap)
    );
    const effectiveMinQueueWidth = Math.min(minQueueWidth, maxQueueWidth);
    return { min: effectiveMinQueueWidth, max: maxQueueWidth };
  }, []);

  const clampTaskQueueWidth = useCallback(
    (candidateWidth) => {
      const { min, max } = getTaskQueueBounds();
      return Math.max(min, Math.min(max, candidateWidth));
    },
    [getTaskQueueBounds]
  );

  const handleTaskQueueResizeStart = useCallback((event) => {
    event.preventDefault();
    isResizingTaskQueueRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  // --- Session selection ---

  const handleSelectSession = (chatSessionId) => {
    if (!isNewChat && chatSessionId === selectedSessionId) {
      setSelectedSessionId(null);
      if (!isPublicMode) {
        useAgentStore.getState().disconnectStream();
        useAgentStore.setState({ sessionDetail: null });
      } else {
        setSelectedPublicSessionToken("");
        setPublicSessionDetail(null);
        setPublicPendingUserMessage(null);
        useAgentStore.setState({ sessionDetail: null, chatStatus: 'ready', sendError: null });
        notifyPublicSessionTokenChange("");
      }
      if (onSessionSelect) onSessionSelect(null);
      return;
    }
    setIsNewChat(false);
    setSelectedSessionId(chatSessionId);
    if (!isPublicMode) {
      useAgentStore.getState().fetchSessionDetail(chatSessionId);
      if (onSessionSelect) onSessionSelect(chatSessionId);
      return;
    }
    const selectedRow = publicSessions.find(
      (session) => session?.chatSessionId === chatSessionId
    );
    const sessionToken =
      publicSessionTokenByChatSessionId[chatSessionId] ||
      selectedRow?.publicSessionToken ||
      "";
    if (!sessionToken) {
      setPublicError("Unable to open this session right now.");
      return;
    }
    setSelectedPublicSessionToken(sessionToken);
    notifyPublicSessionTokenChange(sessionToken);
    if (onSessionSelect) onSessionSelect(chatSessionId);
    loadPublicSessionDetail(sessionToken, { silent: true });
  };

  const handleNewChat = useCallback(() => {
    setIsNewChat(true);
    setSelectedSessionId(null);
    if (!isPublicMode) {
      useAgentStore.getState().disconnectStream();
      useAgentStore.setState({ sessionDetail: null });
    } else {
      setSelectedPublicSessionToken("");
      setPublicSessionDetail(null);
      setPublicPendingUserMessage(null);
      useAgentStore.setState({ sessionDetail: null, chatStatus: 'ready', sendError: null });
      notifyPublicSessionTokenChange("");
    }
    if (onSessionSelect) onSessionSelect(null);
  }, [isPublicMode, notifyPublicSessionTokenChange, onSessionSelect]);

  const handleStartChat = async (message, imageUrls) => {
    if (isPublicMode) {
      if (!publicShareToken || composerDisabled) return;
      setStartingChat(true);
      setIsNewChat(false);
      setSelectedSessionId("__pending__");
      setPublicPendingUserMessage(message);
      setPublicSessionDetail(null);
      try {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const publicStartPayload = {
          ...(publicIdentityPayload || {}),
          message,
          userTimezone,
          agentId,
        };
        if (imageUrls && imageUrls.length > 0) {
          publicStartPayload.imageUrls = imageUrls;
        }
        const response = await usePublicChatStore.getState().startPublicChat(publicShareToken, publicStartPayload);
        const data = response?.data || {};
        const nextPublicSessionToken = data?.publicSessionToken || "";
        if (data?.chatSessionId && nextPublicSessionToken) {
          upsertPublicSessionTokenMapping(
            data.chatSessionId,
            nextPublicSessionToken
          );
          setSelectedPublicSessionToken(nextPublicSessionToken);
          notifyPublicSessionTokenChange(nextPublicSessionToken);
          setSelectedSessionId(data.chatSessionId);
          if (onSessionSelect) onSessionSelect(data.chatSessionId);
          const optimisticUserMsg = {
            messageId: `_optimistic_start_${Date.now()}`,
            role: "user",
            content: message,
            timestamp: new Date().toISOString(),
            imageUrls: imageUrls || undefined,
            senderDisplayName: publicUserDisplayName || undefined,
            _optimistic: true,
          };
          const initialDetail = {
            chatSessionId: data.chatSessionId,
            status: "IN_PROGRESS",
            source: "PUBLIC_CHAT",
            title: agentName || "Agent Chat",
            messages: [optimisticUserMsg],
          };
          setPublicSessionDetail(initialDetail);
          useAgentStore.setState({
            sessionDetail: initialDetail,
            chatStatus: 'submitted',
            sendError: null,
          });
          setPublicError("");
          if (showSidebar) {
            await loadPublicSessions({ silent: true });
          }
          await loadPublicSessionDetail(nextPublicSessionToken, {
            silent: true,
          });
        } else {
          setIsNewChat(true);
          setSelectedSessionId(null);
          setPublicPendingUserMessage(null);
        }
      } catch (error) {
        useAgentStore.setState({ chatStatus: 'ready' });
        setPublicError(
          parsePublicErrorMessage(error, "Unable to start this chat session.")
        );
        setPublicPendingUserMessage(null);
        setIsNewChat(true);
        setSelectedSessionId(null);
      } finally {
        setStartingChat(false);
      }
      return;
    }

    setStartingChat(true);
    setIsNewChat(false);
    setSelectedSessionId("__pending__");
    const result = await useAgentStore.getState().startChat(agentId, message, imageUrls, undefined, undefined, chatContext);
    setStartingChat(false);
    if (result?.chatSessionId) {
      setSelectedSessionId(result.chatSessionId);
      if (onSessionSelect) onSessionSelect(result.chatSessionId);
    } else {
      setIsNewChat(true);
      setSelectedSessionId(null);
    }
  };

  const handleSendPublicMessage = async (message, imageUrls) => {
    if (!isPublicMode || composerDisabled) return;
    const nextMessage = String(message || "").trim();
    if (!nextMessage) return;
    if (!selectedPublicSessionToken) {
      await handleStartChat(nextMessage, imageUrls);
      return;
    }

    const result = await useAgentStore.getState().sendPublicMessage(
      selectedSessionId,
      selectedPublicSessionToken,
      agentId,
      nextMessage,
      imageUrls,
      publicUserDisplayName || undefined,
    );
    if (result) {
      setPublicError("");
      if (showSidebar) {
        loadPublicSessions({ silent: true });
      }
    }
  };

  // --- Effects ---

  const initialLoadDone = useRef(false);
  const previousNewChatRequestNonceRef = useRef(newChatRequestNonce);
  const prevStatusRef = useRef(storeSessionDetail?.status);

  useEffect(() => {
    if (newChatRequestNonce === previousNewChatRequestNonceRef.current) {
      return;
    }
    previousNewChatRequestNonceRef.current = newChatRequestNonce;
    handleNewChat();
  }, [handleNewChat, newChatRequestNonce]);

  const previousSelectSessionNonceRef = useRef(selectSessionRequest?.nonce ?? 0);
  useEffect(() => {
    if (!selectSessionRequest) return;
    if (selectSessionRequest.nonce === previousSelectSessionNonceRef.current) return;
    previousSelectSessionNonceRef.current = selectSessionRequest.nonce;
    if (selectSessionRequest.sessionId) {
      handleSelectSession(selectSessionRequest.sessionId);
    }
  }, [selectSessionRequest]);

  useEffect(() => {
    if (isPublicMode) return;
    if (initialSessionId && !initialLoadDone.current) {
      initialLoadDone.current = true;
      useAgentStore.getState().fetchSessionDetail(initialSessionId);
    }
  }, [initialSessionId, isPublicMode]);

  useEffect(() => {
    return () => useAgentStore.getState().disconnectStream();
  }, []);

  useEffect(() => {
    if (isPublicMode) {
      if (!selectedPublicSessionToken || !selectedSessionId || selectedSessionId === "__pending__") {
        return;
      }
      useAgentStore.getState().subscribeToSession(selectedSessionId, {
        publicSessionToken: selectedPublicSessionToken,
        reconnectWhileSubscribed: true,
      });
      return () => useAgentStore.getState().disconnectStream();
    }
    if (!selectedSessionId || selectedSessionId === "__pending__") return;
    useAgentStore.getState().subscribeToSession(selectedSessionId, {
      reconnectWhileSubscribed: true,
    });
    return () => useAgentStore.getState().disconnectStream();
  }, [isPublicMode, selectedSessionId, selectedPublicSessionToken]);

  useEffect(() => {
    if (isPublicMode) return;
    if (!agentId) return;
    useAgentStore.getState().subscribeToAgent(agentId);
    return () => useAgentStore.getState().disconnectAgentStream();
  }, [agentId, isPublicMode]);

  useEffect(() => {
    if (!isPublicMode || !showSidebar) return;
    const cb = () => loadPublicSessions({ silent: true });
    useAgentStore.getState().setPublicSessionChangeCallback(cb);
    return () => useAgentStore.getState().setPublicSessionChangeCallback(null);
  }, [isPublicMode, showSidebar, loadPublicSessions]);

  useEffect(() => {
    if (isPublicMode) return;
    if (!agentId) return;
    const id = setInterval(() => {
      useAgentStore.getState().fetchSessions(agentId, { silent: true });
    }, 10000);
    return () => clearInterval(id);
  }, [agentId, isPublicMode]);

  useEffect(() => {
    if (!isPublicMode || !publicShareToken || !showSidebar) return;
    loadPublicSessions();
  }, [isPublicMode, publicShareToken, loadPublicSessions, showSidebar]);

  useEffect(() => {
    if (!isPublicMode || !publicShareToken || !showSidebar) return;
    const id = setInterval(() => {
      loadPublicSessions({ silent: true });
    }, 10000);
    return () => clearInterval(id);
  }, [isPublicMode, publicShareToken, loadPublicSessions, showSidebar]);

  useEffect(() => {
    if (!isPublicMode) return;
    const nextToken = initialPublicSessionToken || "";
    if (!nextToken) return;
    setSelectedPublicSessionToken(nextToken);
    loadPublicSessionDetail(nextToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPublicSessionToken, isPublicMode]);

  // Public session polling replaced by SSE subscription above.

  useEffect(() => {
    if (
      (!effectiveChatUiPolicy.showSchedulingActivityInChat || isPublicMode) &&
      sidebarTab === "scheduled"
    ) {
      setSidebarTab("sessions");
    }
  }, [
    effectiveChatUiPolicy.showSchedulingActivityInChat,
    isPublicMode,
    sidebarTab,
  ]);

  useEffect(() => {
    if (isPublicMode) return;
    const currentStatus = storeSessionDetail?.status;
    if (
      prevStatusRef.current !== currentStatus &&
      selectedSessionId &&
      selectedSessionId !== "__pending__"
    ) {
      useAgentStore.getState().refreshSessionInList(selectedSessionId);
      if (
        prevStatusRef.current === "IN_PROGRESS" &&
        currentStatus &&
        currentStatus !== "IN_PROGRESS"
      ) {
        useAgentStore.getState().fetchSessions(agentId, { silent: true });
      }
    }
    prevStatusRef.current = currentStatus;
  }, [
    storeSessionDetail?.status,
    selectedSessionId,
    agentId,
    isPublicMode,
  ]);

  // Resize effects

  useEffect(() => {
    const node = panelRootRef.current;
    if (!node) return undefined;

    const updateFromContainer = () => {
      setSidebarWidth((prev) => clampSidebarWidth(prev));
      tq.setTaskQueuePanelWidth((prev) => clampTaskQueueWidth(prev));
    };

    updateFromContainer();

    if (typeof ResizeObserver === "function") {
      const resizeObserver = new ResizeObserver(updateFromContainer);
      resizeObserver.observe(node);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener("resize", updateFromContainer);
    return () => window.removeEventListener("resize", updateFromContainer);
  }, [clampSidebarWidth, clampTaskQueueWidth, tq]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!isResizingSidebarRef.current && !isResizingTaskQueueRef.current)
        return;
      const containerRect = panelRootRef.current?.getBoundingClientRect();
      if (!containerRect) return;
      if (isResizingSidebarRef.current) {
        const nextSidebarWidth = event.clientX - containerRect.left;
        setSidebarWidth(clampSidebarWidth(nextSidebarWidth));
      }
      if (isResizingTaskQueueRef.current) {
        const nextQueueWidth = containerRect.right - event.clientX;
        tq.setTaskQueuePanelWidth(clampTaskQueueWidth(nextQueueWidth));
      }
    };

    const stopResize = () => {
      if (!isResizingSidebarRef.current && !isResizingTaskQueueRef.current)
        return;
      isResizingSidebarRef.current = false;
      isResizingTaskQueueRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopResize);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [clampSidebarWidth, clampTaskQueueWidth, tq]);

  useEffect(() => {
    setSidebarWidth((prev) => clampSidebarWidth(prev));
    tq.setTaskQueuePanelWidth((prev) => clampTaskQueueWidth(prev));
  }, [
    storeSessionDetail,
    publicSessionDetail,
    selectedSessionId,
    clampSidebarWidth,
    clampTaskQueueWidth,
    tq,
  ]);

  // --- Derived values for render ---

  const sessions = isPublicMode ? publicSessions : storeSessions;
  const sessionsLoading = isPublicMode
    ? publicSessionsLoading
    : storeSessionsLoading;
  const sessionsHasMore = isPublicMode ? false : storeSessionsHasMore;
  const sessionsLoadingMore = isPublicMode ? false : storeSessionsLoadingMore;

  const handleSessionsListScroll = useCallback(
    (e) => {
      if (!sessionsHasMore || sessionsLoadingMore || !agentId) return;
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
        useAgentStore.getState().fetchMoreSessions(agentId);
      }
    },
    [sessionsHasMore, sessionsLoadingMore, agentId]
  );
  const sessionDetail = isPublicMode
    ? (storeSessionDetail || publicSessionDetail)
    : storeSessionDetail;
  const sessionDetailLoading = isPublicMode
    ? (storeSessionDetailLoading || publicSessionDetailLoading)
    : storeSessionDetailLoading;
  const conversationCapabilities = buildAgentSessionCapabilities(
    effectiveChatUiPolicy,
    isPublicMode
  );
  const sidebarTabs =
    effectiveChatUiPolicy.showSchedulingActivityInChat && !isPublicMode
      ? [
          { key: "sessions", label: "Sessions" },
          { key: "scheduled", label: "Scheduled" },
        ]
      : [{ key: "sessions", label: "Sessions" }];
  const hasActiveConversation =
    Boolean(selectedSessionId) &&
    selectedSessionId !== "__pending__" &&
    Boolean(sessionDetail);
  const showTaskQueueToggle =
    taskQueuePlannerUiEnabled &&
    hasActiveConversation &&
    (!isPublicMode || Boolean(selectedPublicSessionToken));

  useEffect(() => {
    if (typeof onTaskQueueToggleStateChange !== "function") return;
    onTaskQueueToggleStateChange({
      visible: showTaskQueueToggle,
      open: showTaskQueueToggle && tq.taskQueuePanelOpen,
    });
  }, [onTaskQueueToggleStateChange, showTaskQueueToggle, tq.taskQueuePanelOpen]);

  const taskQueuePanelConnectionState = tq.taskQueueRevisionSyncing
    ? "reconciling"
    : tq.taskQueueConnectionState;
  const useStackedQueueLayout =
    showTaskQueueToggle && tq.taskQueuePanelOpen && isNarrowQueueMode;
  const useFullWidthTaskQueueLayout =
    showTaskQueueToggle &&
    tq.taskQueuePanelOpen &&
    tq.taskQueueExpandedMode &&
    !useStackedQueueLayout;

  // --- Render ---

  const taskQueuePanelProps = {
    snapshot: tq.taskQueueSnapshot,
    loading: tq.taskQueueLoading,
    applying: tq.taskQueueApplying,
    error: tq.taskQueueError,
    connectionState: taskQueuePanelConnectionState,
    queueHistory: tq.taskQueueHistory,
    selectedQueueId: tq.selectedQueueId,
    onSelectQueue: tq.handleSelectQueue,
    onRefresh: () => tq.loadTaskQueueSnapshot({ silent: false }),
    onAction: tq.handleApplyTaskQueueAction,
    onClose: tq.handleCloseTaskQueuePanel,
    onExpandedModeChange: tq.handleTaskQueueExpandedModeChange,
  };

  return (
    <PanelRoot ref={panelRootRef}>
      {showSidebar && (
        <>
          <Sidebar sx={{ width: `${sidebarWidth}px` }}>
            <Box
              sx={{
                px: 2,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                height: "57px",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ display: "flex", gap: 0, flex: 1 }}>
                {sidebarTabs.map((tab) => (
                  <Box
                    key={tab.key}
                    onClick={() => setSidebarTab(tab.key)}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      cursor: "pointer",
                      borderBottom: 2,
                      borderColor:
                        sidebarTab === tab.key
                          ? "text.primary"
                          : "transparent",
                      transition: "all 150ms ease",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: sidebarTab === tab.key ? 600 : 500,
                        color: sidebarTab === tab.key ? "text.primary" : "text.secondary",
                      }}
                    >
                      {tab.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {sidebarTab === "sessions" && (
                <IconButton
                  onClick={handleNewChat}
                  size="small"
                  sx={{
                    color: "text.disabled",
                    "&:hover": { backgroundColor: "action.hover", color: "text.primary" },
                  }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
            </Box>

            <SidebarList onScroll={sidebarTab === "sessions" ? handleSessionsListScroll : undefined}>
              {sidebarTab === "sessions" ? (
                sessionsLoading ? (
                  <Box sx={{ p: 2 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Skeleton variant="text" width="80%" height={18} />
                        <Skeleton variant="text" width="50%" height={14} />
                      </Box>
                    ))}
                  </Box>
                ) : sessions.length === 0 && !isNewChat ? (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "text.secondary",
                        lineHeight: 1.6,
                      }}
                    >
                      No sessions yet. Click + to start a conversation.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {sessions.map((s) => (
                      <SessionRow
                        key={s.chatSessionId}
                        session={s}
                        selected={
                          !isNewChat && s.chatSessionId === selectedSessionId
                        }
                        onClick={() => handleSelectSession(s.chatSessionId)}
                        showWorkflowProvenanceInChat={
                          effectiveChatUiPolicy.showWorkflowProvenanceInChat
                        }
                      />
                    ))}
                    {sessionsLoadingMore && (
                      <Box sx={{ p: 2 }}>
                        {[1, 2].map((i) => (
                          <Box key={i} sx={{ mb: 2 }}>
                            <Skeleton variant="text" width="80%" height={18} />
                            <Skeleton variant="text" width="50%" height={14} />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )
              ) : (
                <ScheduledTab agentId={agentId} />
              )}
            </SidebarList>
          </Sidebar>
          <Box
            sx={{
              width: 0,
              flexShrink: 0,
              position: "relative",
              overflow: "visible",
              zIndex: 2,
            }}
          >
            <Box
              onMouseDown={handleSidebarResizeStart}
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: "-3px",
                width: `${SIDEBAR_RESIZE_HANDLE_WIDTH}px`,
                cursor: "col-resize",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "1px",
                  backgroundColor: "divider",
                  pointerEvents: "none",
                  transition: "background-color 150ms, width 150ms",
                },
                "&:hover::after": {
                  backgroundColor: "text.secondary",
                  width: "3px",
                  borderRadius: "2px",
                },
              }}
            />
          </Box>
        </>
      )}

      <ChatArea>
        {isPublicMode && publicError && (
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: `1px solid ${_sp.red.border}`,
              backgroundColor: _sp.red.bg,
            }}
          >
            <Typography sx={{ fontSize: "12px", color: _sp.red.color }}>
              {publicError}
            </Typography>
          </Box>
        )}
        {isNewChat ? (
          <NewChatComposer
            onSend={handleStartChat}
            disabled={startingChat || (isPublicMode && composerDisabled)}
            agentName={agentName}
            agentDescription={agentDescription}
            agentInboxAddress={isPublicMode ? "" : agentInboxAddress}
            agentId={agentId}
            agentModel={agentModel}
            onConfigOpen={isPublicMode ? undefined : handleOpenAgentSettings}
            showSessionOperationalMetaInChat={
              effectiveChatUiPolicy.showSessionOperationalMetaInChat
            }
            welcomePanelContent={welcomePanelContent}
            hideComposerInput={hideWelcomeComposerInput}
          />
        ) : selectedSessionId === "__pending__" ? (
          <PendingChatView
            agentName={agentName}
            agentId={agentId}
            onOpenAgentSettings={
              isPublicMode ? undefined : handleOpenAgentSettings
            }
            pendingMessage={isPublicMode ? publicPendingUserMessage : undefined}
            forceUserNameAsYou={effectiveChatUiPolicy.forceUserNameAsYou}
            publicUserDisplayName={publicUserDisplayName}
          />
        ) : !selectedSessionId ? (
          <NewChatComposer
            onSend={handleStartChat}
            disabled={startingChat || (isPublicMode && composerDisabled)}
            agentName={agentName}
            agentDescription={agentDescription}
            agentInboxAddress={isPublicMode ? "" : agentInboxAddress}
            agentId={agentId}
            agentModel={agentModel}
            onConfigOpen={isPublicMode ? undefined : handleOpenAgentSettings}
            showSessionOperationalMetaInChat={
              effectiveChatUiPolicy.showSessionOperationalMetaInChat
            }
            welcomePanelContent={welcomePanelContent}
            hideComposerInput={hideWelcomeComposerInput}
          />
        ) : sessionDetailLoading && !sessionDetail ? (
          <Box sx={{ p: 3 }}>
            <Skeleton variant="text" width={200} height={22} sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Skeleton
                variant="rectangular"
                height={48}
                sx={{ borderRadius: "12px", maxWidth: "60%" }}
              />
              <Skeleton
                variant="rectangular"
                height={36}
                sx={{
                  borderRadius: "12px",
                  maxWidth: "45%",
                  alignSelf: "flex-end",
                }}
              />
              <Skeleton
                variant="rectangular"
                height={64}
                sx={{ borderRadius: "12px", maxWidth: "55%" }}
              />
            </Box>
          </Box>
        ) : sessionDetail ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: useStackedQueueLayout ? "column" : "row",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {useStackedQueueLayout && (
              <Box
                sx={{
                  px: 1,
                  py: 0.75,
                  borderBottom: 1,
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <Box
                  onClick={() => tq.setMobileQueueTab("chat")}
                  sx={{
                    px: 1.5,
                    py: 0.45,
                    borderRadius: "999px",
                    border: "1px solid",
                    borderColor:
                      tq.mobileQueueTab === "chat" ? "divider" : "transparent",
                    backgroundColor:
                      tq.mobileQueueTab === "chat" ? "action.hover" : "transparent",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: tq.mobileQueueTab === "chat" ? "text.primary" : "text.disabled",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  Chat
                </Box>
                <Box
                  onClick={() => tq.setMobileQueueTab("queue")}
                  sx={{
                    px: 1.5,
                    py: 0.45,
                    borderRadius: "999px",
                    border: "1px solid",
                    borderColor:
                      tq.mobileQueueTab === "queue" ? "divider" : "transparent",
                    backgroundColor:
                      tq.mobileQueueTab === "queue" ? "action.hover" : "transparent",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: tq.mobileQueueTab === "queue" ? "text.primary" : "text.disabled",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  Task Queue
                </Box>
              </Box>
            )}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                ...(useStackedQueueLayout &&
                  tq.mobileQueueTab !== "chat" && { display: "none" }),
                ...(useFullWidthTaskQueueLayout && { display: "none" }),
              }}
            >
              <ConversationWorkspace
                session={sessionDetail}
                agentId={agentId}
                agentName={agentName}
                agentModel={agentModel}
                agentInboxAddress={isPublicMode ? "" : agentInboxAddress}
                capabilities={conversationCapabilities}
                chatUiPolicy={effectiveChatUiPolicy}
                headerActions={headerActions}
                onOpenAgentSettings={
                  isPublicMode ? undefined : handleOpenAgentSettings
                }
                onSendMessage={isPublicMode ? handleSendPublicMessage : undefined}
                allowScheduling={!isPublicMode}
                allowStop={!isPublicMode}
                composerDisabled={Boolean(isPublicMode && composerDisabled)}
                debugModeEnabled={debugModeEnabled && !isPublicMode}
              />
            </Box>

            {inlineTaskQueueToggle && showTaskQueueToggle && !tq.taskQueuePanelOpen && (
              <Box
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 12,
                  zIndex: 5,
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ChecklistOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={tq.handleOpenTaskQueuePanel}
                  sx={{
                    textTransform: "none",
                    borderColor: "divider",
                    color: "text.secondary",
                    backgroundColor: "background.paper",
                    "&:hover": {
                      borderColor: "text.secondary",
                      backgroundColor: "background.subtle",
                    },
                  }}
                >
                  Task Queue
                </Button>
              </Box>
            )}

            {showTaskQueueToggle && tq.taskQueuePanelOpen && (
              <>
                {!useStackedQueueLayout ? (
                  <>
                    {!useFullWidthTaskQueueLayout && (
                      <Box
                        onMouseDown={handleTaskQueueResizeStart}
                        sx={{
                          width: `${TASK_QUEUE_RESIZE_HANDLE_WIDTH}px`,
                          cursor: "col-resize",
                          flexShrink: 0,
                          backgroundColor: "action.hover",
                          transition: "background-color 120ms ease",
                          "&:hover": { backgroundColor: "action.selected" },
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        width: useFullWidthTaskQueueLayout
                          ? "auto"
                          : `${tq.taskQueuePanelWidth}px`,
                        maxWidth: useFullWidthTaskQueueLayout ? "none" : "50%",
                        flex: useFullWidthTaskQueueLayout ? 1 : `0 1 ${tq.taskQueuePanelWidth}px`,
                        minWidth: 0,
                        borderLeft: useFullWidthTaskQueueLayout
                          ? "none"
                          : 1,
                        borderColor: useFullWidthTaskQueueLayout
                          ? "transparent"
                          : "divider",
                        backgroundColor: "background.paper",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                      }}
                    >
                      <TaskQueuePanel {...taskQueuePanelProps} />
                    </Box>
                  </>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      minHeight: 0,
                      borderTop: 1,
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      display: tq.mobileQueueTab === "queue" ? "flex" : "none",
                      flexDirection: "column",
                    }}
                  >
                    <TaskQueuePanel {...taskQueuePanelProps} />
                  </Box>
                )}
              </>
            )}
          </Box>
        ) : null}
      </ChatArea>
    </PanelRoot>
  );
}

export default AgentSessionsPanel;
